import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import {
    groupsForStudent,
    GroupState,
    ListResponse,
    StudentGroupState,
} from './../types'
import baseQueryWithReauth from '../../app/baseQueryWithOnlineCourse'
import { getGroupsBySubjectsAndLanguage } from './groupActions'

interface getGroupStudentsParams {
    groupId: number
    params: string
}

interface addStudentToGroupParams {
    studentGroupId: number
    courseUserEnrollId: number
    courseSubjectId?: any
}

export const groupApiSlice = createApi({
    reducerPath: 'groupApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Group', 'GroupStudent', 'NoGroupStudent'],
    endpoints: (build) => ({
        fetchAllGroups: build.query<any, number>({
            query: (courseId) => ({
                url: `student-group`,
                params: { courseId },
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'Group' as const,
                            id,
                        })),
                        { type: 'Group', id: 'LIST' },
                    ]
                    : [{ type: 'Group', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getGroupById: build.query<any, number>({
            query: (id) => ({
                url: `student-group/${id}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        createGroup: build.mutation<void, GroupState>({
            query: (group) => ({
                url: 'student-group',
                method: 'POST',
                body: group,
            }),
            invalidatesTags: [{ type: 'Group', id: 'LIST' }],
        }),

        updateGroup: build.mutation<void, GroupState>({
            query(data) {
                const { id, ...body } = data
                return {
                    url: `student-group/${id}`,
                    method: 'PUT',
                    body,
                }
            },
            invalidatesTags: (result, error, { id }) => [{ type: 'Group', id }],
        }),

        deleteGroup: build.mutation<void, number>({
            query: (id) => ({
                url: `/student-group/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Group', id }],
        }),

        getGroupsForStudent: build.query<any, groupsForStudent>({
            query: (params) => ({
                url: `student-group/get-for-student?subjectIds=${params.subjectIds}&languageEnum=${params.languageEnum}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'Group' as const,
                            id,
                        })),
                        { type: 'Group', id: 'LIST' },
                    ]
                    : [{ type: 'Group', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getGroupsForTeacher: build.query<
            GroupState[],
            { courseSubjectId: number; languageEnum: string }
        >({
            query: ({ courseSubjectId, languageEnum }) => ({
                url: `student-group/get-for-teacher?courseSubjectId=${courseSubjectId}&languageEnum=${languageEnum}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(({ id }) => ({
                            type: 'Group' as const,
                            id,
                        })),
                        { type: 'Group', id: 'LIST' },
                    ]
                    : [{ type: 'Group', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        fetchAllGroupStudents: build.query<
            ListResponse<StudentGroupState>,
            getGroupStudentsParams
        >({
            query: (props) => ({
                url: `student-group/students/${props.groupId}?${props.params}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map(({ userId }) => ({
                            type: 'GroupStudent' as const,
                            userId,
                        })),
                        { type: 'GroupStudent', id: 'LIST' },
                    ]
                    : [{ type: 'GroupStudent', id: 'LIST' }],
            transformResponse: (response: {
                data: ListResponse<StudentGroupState>
            }) => ({
                data: response.data.data,
                pageNumber: response.data.pageNumber,
                totalCount: response.data.totalCount,
                totalPages: response.data.totalPages,
            }),
        }),

        fetchAllGroupStudentsNoGroup: build.query<
            ListResponse<StudentGroupState>,
            getGroupStudentsParams
        >({
            query: (props) => ({
                url: `student-group/other-students/${props.groupId}?${props.params}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.data.map(({ userId }) => ({
                            type: 'NoGroupStudent' as const,
                            userId,
                        })),
                        { type: 'NoGroupStudent', id: 'LIST' },
                    ]
                    : [{ type: 'NoGroupStudent', id: 'LIST' }],
            transformResponse: (response: {
                data: ListResponse<StudentGroupState>
            }) => ({
                data: response.data.data,
                pageNumber: response.data.pageNumber,
                totalCount: response.data.totalCount,
                totalPages: response.data.totalPages,
            }),
        }),

        addStudentToGroup: build.mutation<void, addStudentToGroupParams>({
            query: (params) => ({
                url: `student-group/change/${params.courseUserEnrollId}`,
                method: 'PUT',
                body: {
                    studentGroupId: params?.studentGroupId,
                    courseSubjectId: params?.courseSubjectId
                },
            }),
            invalidatesTags: [
                { type: 'GroupStudent', id: 'LIST' },
                { type: 'NoGroupStudent', id: 'LIST' },
            ],
        }),
    }),
})

const initialState = {
    loading: false,
    groups: [],
    error: false,
    success: false,
}

const groupSlice = createSlice({
    name: 'group',
    initialState,
    reducers: {
        setGroups: (state, action: PayloadAction<[]>) => {
            state.groups = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getGroupsBySubjectsAndLanguage.pending, (state) => {
            state.loading = true
            state.error = false
            state.success = false
        }),
            builder.addCase(
                getGroupsBySubjectsAndLanguage.fulfilled,
                (state, action) => {
                    state.loading = false
                    state.error = false
                    state.success = true
                    state.groups = action.payload
                },
            ),
            builder.addCase(
                getGroupsBySubjectsAndLanguage.rejected,
                (state) => {
                    state.loading = false
                    state.error = true
                    state.success = false
                },
            )
    },
})

export default groupSlice.reducer

export const { setGroups } = groupSlice.actions
